// A fighter's battery, as the server fires it (internal/game/world.go
// fireGun, config.go resolveBattery): one mount per gun type — the
// Spitfire IX's two Hispanos and four .303s, the Bf 109 G's MK 108 down the
// spinner and the two MG 131s over the cowl — each with its caliber and the
// body-frame tip of every barrel (nose +X, up +Y, starboard +Z). The first
// mount is the sighting mount: the gunsight is ranged for its rounds, and the
// server lays every other mount's barrels on that mount's point of impact.
//
// The legacy block (one caliber, `barrels` of it — the jets era, the ad
// circus) reads as one mount at its old muzzles: 1.2 m ahead of the origin,
// 0.3 m under it, a pair at ±wingSpanM·0.28 (a quad a foot either side of
// it), a single at mountOffsetM.

// Where the legacy block's muzzles sit (gunnery.js GUN_MUZZLE_FWD_M / _DOWN_M).
const LEGACY_FWD_M = 1.2;
const LEGACY_DOWN_M = 0.3;
// The gap between paired barrels of a legacy multi-gun wing (world.go
// gunBarrelGapM).
const LEGACY_GAP_M = 0.3;

/**
 * The battery's mounts: [{ caliber (the config entry, or {} for a name the
 * table doesn't carry), name, at: [[x,y,z], ...], volleySec }], sighting
 * mount first. `calByName` maps caliber name → config caliber. [] for a type
 * with no gun block.
 */
export function batteryOf(type, calByName) {
  const g = type?.gun;
  if (!g) return [];
  const calOf = (n) => calByName?.get?.(n) ?? {};
  if (Array.isArray(g.mounts) && g.mounts.length) {
    return g.mounts.map((m) => {
      const caliber = calOf(m.caliber);
      return { caliber, name: m.caliber, at: m.at ?? [], volleySec: 2 / (caliber.roundsPerSec || 10) };
    });
  }
  const caliber = calOf(g.caliber);
  const barrels = g.barrels > 0 ? g.barrels : 2;
  const rps = caliber.roundsPerSec || 10;
  if (barrels === 1) {
    return [{ caliber, name: g.caliber, at: [[LEGACY_FWD_M, -LEGACY_DOWN_M, g.mountOffsetM ?? 0]], volleySec: 1 / rps }];
  }
  const wing = (type.wingSpanM ?? 0) * 0.28;
  const per = barrels / 2;
  const at = [];
  for (const side of [-1, 1]) {
    for (let k = 0; k < per; k++) at.push([LEGACY_FWD_M, -LEGACY_DOWN_M, side * wing + (k - (per - 1) / 2) * LEGACY_GAP_M]);
  }
  return [{ caliber, name: g.caliber, at, volleySec: 2 / rps }];
}

/** Every barrel tip of the battery, sighting mount first: the flash list. */
export function batteryMuzzles(type, calByName) {
  return batteryOf(type, calByName).flatMap((m) => m.at);
}

/** The caliber the gunsight is ranged for (the sighting mount's). */
export function sightingCaliber(type, calByName) {
  return batteryOf(type, calByName)[0]?.caliber ?? {};
}

/**
 * The sighting mount's mean muzzle in the body frame ({x, y}), which is where
 * gunSightLine's rounds leave from: the server aims those barrels at the
 * convergence point, and lays the rest of the battery on where they land.
 */
export function sightingMuzzle(type, calByName) {
  const at = batteryOf(type, calByName)[0]?.at ?? [];
  if (!at.length) return { x: LEGACY_FWD_M, y: -LEGACY_DOWN_M };
  let x = 0;
  let y = 0;
  for (const a of at) {
    x += a[0];
    y += a[1];
  }
  return { x: x / at.length, y: y / at.length };
}

/** Weight of fire: damage a second with every round on target (config.go GunDPS). */
export function batteryDps(type, calByName) {
  let dps = 0;
  for (const m of batteryOf(type, calByName)) dps += ((m.caliber.damage ?? 0) * m.at.length) / m.volleySec;
  return dps;
}

/** How many barrels the battery fires in all. */
export function batteryBarrels(type, calByName) {
  return batteryOf(type, calByName).reduce((n, m) => n + m.at.length, 0);
}

/**
 * The battery as a pilot reads it off the hangar card: "2 × 20 mm Hispano +
 * 4 × .303 Browning". A caliber's `gun` names the weapon; its `label` stands
 * in for one that doesn't.
 */
export function batteryLabel(type, calByName) {
  return batteryOf(type, calByName)
    .map((m) => `${m.at.length} × ${m.caliber.gun ?? m.caliber.label ?? m.name}`)
    .join(" + ");
}

/**
 * The spanwise reach of the battery's outermost barrel (m): how far apart a
 * wing battery's streams start, which the GUNS row's sketch and its advice
 * reason from. A nose battery reads well under a metre.
 */
export function batterySpan(type, calByName) {
  let w = 0;
  for (const a of batteryMuzzles(type, calByName)) w = Math.max(w, Math.abs(a[2]));
  return w;
}
